/**
 Project	ENT - Environnement numérique de travail

 @file 	    services.service.ts
 @author  	Martin Tazlari <martin.tazlari@ictvs.ch>
 @copyright © 2020, ICT-vs. All rights reserved.
 **/

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom, map } from 'rxjs';
import { DefaultResponse } from 'src/app/models/services/default-reponse.model';
import { Injectable } from '@angular/core';
import { Log } from 'src/app/models/services/logs/add-log.model';
import { MultiTaggedServiceResponse } from 'src/app/models/services/taggedServices/taggedService-service-response.model';
import { TaggedService } from 'src/app/models/taggedService.model';
import { UserInfo } from 'src/app/models/user-info.model';
import { environment } from 'src/environments/environment';
import { LogService } from '../logs/log.service';
import { UsersService } from '../users/users.service';
import { LocalStorageService } from '../localStorage/localStorage.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class Services {
  selectedUser: UserInfo;
  constructor(
    private _logService: LogService,
    private _userService: UsersService,
    private http: HttpClient,
    private _localStorageService: LocalStorageService
  ) {
    this.getUser();
  }

  async getUser() {
    if (this._localStorageService.getItem<UserInfo>('currentUser') === null) {
      this.selectedUser = await lastValueFrom(
        this._userService.getCurrentUser()
      );
    } else {
      this.selectedUser = this._userService.currentUserValue;
    }
  }

  openService(url: string, name: string) {
    lastValueFrom(
      this._logService.addLog(
        new Log(
          'SUCCESS',
          this.selectedUser.oid,
          this.selectedUser.oid,
          'openService()',
          name,
          this._localStorageService.getItem<string>('userDefaultGE'),
          this.selectedUser.roles.map(role => role.name)
        )
      )
    );
    window.open(url, 'ENTServiceTab', 'noopener');
  }

  openDocumentation(url: string): void {
    lastValueFrom(
      this._logService.addLog(
        new Log(
          'SUCCESS',
          this.selectedUser.oid,
          this.selectedUser.oid,
          'openDocumentation()',
          url,
          this._localStorageService.getItem<string>('userDefaultGE'),
          this.selectedUser.roles.map(role => role.name)
        )
      )
    );
    window.open(url);
  }

  /**
   * Get user tagged services by id
   * @param userid
   * @returns
   */
  getTaggedServices(userId: string): Observable<TaggedService[]> {
    return this.http
      .get<MultiTaggedServiceResponse>(
        environment.apiUrl + '/services/taggedservices/' + userId
      )
      .pipe(
        map(response => {
          return response.taggedService;
        })
      );
  }

  /**
   * Delete a tagged service by id
   * @param taggedService
   * @returns
   */
  deleteTaggedService(
    taggedService: TaggedService
  ): Observable<DefaultResponse> {
    return this.http.delete<DefaultResponse>(
      environment.apiUrl +
        '/services/taggedservices?serviceOid=' +
        taggedService.serviceOid
    );
  }

  /**
   * Reset all tagged services
   * @returns
   */
  resetTaggedServices(): Observable<DefaultResponse> {
    return this.http.delete<DefaultResponse>(
      environment.apiUrl + '/services/reset'
    );
  }

  /**
   * Add a tagged service
   * @param taggedService
   * @returns
   */
  addTaggedService(taggedService: TaggedService): Observable<DefaultResponse> {
    return this.http.put<DefaultResponse>(
      environment.apiUrl + '/services/taggedservices',
      taggedService,
      httpOptions
    );
  }
}
